import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, ArrowLeft, ArrowRight, Loader2, Calendar } from "lucide-react";

import type { Easing } from "framer-motion";
const ease: Easing = [0.25, 0.1, 0.25, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5, ease } }),
};

export default function Blog() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from("blog_posts")
      .select("*")
      .eq("status", "published")
      .order("published_at", { ascending: false })
      .then(({ data }) => {
        setPosts(data || []);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <nav className="fixed top-0 inset-x-0 z-50 glass-card border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <MessageSquare className="w-7 h-7 text-primary" />
            <span className="text-xl font-bold font-headline tracking-tight text-primary">SYRA</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Voltar
          </Button>
        </div>
      </nav>

      <div className="pt-28 pb-20 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div className="text-center mb-12" initial="hidden" animate="visible" variants={fadeUp} custom={0}>
            <h1 className="text-4xl sm:text-5xl font-extrabold font-headline mb-4">Blog</h1>
            <p className="text-muted-foreground text-lg">Insights, novidades e dicas sobre gestão de comunidades com IA.</p>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">Nenhum post publicado ainda. Volte em breve!</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {posts.map((post, i) => (
                <motion.div key={post.id} initial="hidden" animate="visible" variants={fadeUp} custom={i + 1}>
                  <Card className="h-full hover:border-primary/30 transition-colors cursor-pointer group" onClick={() => navigate(`/blog/${post.slug}`)}>
                    {post.cover_image_url && (
                      <div className="aspect-video overflow-hidden rounded-t-lg">
                        <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      </div>
                    )}
                    <CardContent className="p-6 space-y-3">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {post.published_at ? new Date(post.published_at).toLocaleDateString("pt-BR") : ""}
                        <span>•</span>
                        <span>{post.author_name}</span>
                      </div>
                      <h2 className="font-bold font-headline text-lg group-hover:text-primary transition-colors">{post.title}</h2>
                      <p className="text-sm text-muted-foreground line-clamp-3">{post.excerpt}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(Array.isArray(post.tags) ? post.tags : []).map((tag: string, j: number) => (
                          <Badge key={j} variant="outline" className="text-xs">{tag}</Badge>
                        ))}
                      </div>
                      <div className="flex items-center text-primary text-sm font-medium">
                        Ler mais <ArrowRight className="ml-1 h-3 w-3" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
